import { useEffect, useState } from 'react';
import { Confirm, useDataProvider, useRefresh } from 'react-admin';

import { useErrorHandling } from '@app/hooks/useErrorHandling';
import { TableCell } from '@mui/material';
import Switch from '@mui/material/Switch';

import { ColumnInterface, GenericObject } from '@app/components/common/Table/DefaultTable/types';

import './styles.css';

interface StatusCellProps {
  resource?: string;
  row: GenericObject;
  column: ColumnInterface;
}

/**
 * Can handle row.status === "ACTIVE" or "INACTIVE", and also
 * row.active === true or false
 */
export const StatusCell = ({ row, column, resource }: StatusCellProps) => {
  const [handleError] = useErrorHandling();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const [checked, setChecked] = useState(column?.isPublished ? column?.isPublished(row) : false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (row.active !== undefined) {
    if (row.active === true) {
      row.status = 'ACTIVE';
    } else {
      row.status = 'INACTIVE';
    }
  }

  useEffect(() => {
    setChecked(column?.isPublished ? column?.isPublished(row) : false);
  }, [column, row]);

  const togglePublish = async () => {
    if (!column.getNewPublishedStatus || !column.publishedKey || !column.isPublished || !resource) {
      return;
    }

    handleDialogClose();
    try {
      setLoading(true);
      await dataProvider.update(resource, {
        id: row.id as number,
        data: { id: row.id, [column.publishedKey as string]: column?.getNewPublishedStatus(row) },
        previousData: row,
      });

      refresh();
      column.posStatusChange && column.posStatusChange();
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => setConfirmOpen(false);

  const handleOpenConfirm = () => {
    if (column.validation && !column.validation(row)) {
      return;
    }

    setConfirmOpen(true);
  };

  return (
    <>
      <TableCell align="left">
        <Switch checked={checked} onChange={handleOpenConfirm} inputProps={{ 'aria-label': 'status' }} />
      </TableCell>
      <Confirm
        isOpen={confirmOpen}
        loading={loading}
        title={`${column?.isPublished && !column?.isPublished(row) ? 'Publish' : 'Unpublish'} item`}
        content={`Are you sure you want to ${
          column?.isPublished && !column?.isPublished(row) ? 'publish' : 'unpublish'
        } this item?`}
        onConfirm={togglePublish}
        cancel="Cancel"
        confirm={column?.isPublished && !column?.isPublished(row) ? 'Publish' : 'Unpublish'}
        onClose={handleDialogClose}
      />
    </>
  );
};
